import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Client } from '@stomp/stompjs';
import { AdminRideChatMessageDto, AdminRideDetailsDto, AdminRideReviewDto, getAdminRide, getAdminRideChat, getApiBaseUrl, useApiClient } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { AdminRideRouteMap } from '../components/AdminRideRouteMap';
import { InfoTile, MetricCard, PageHeader, PageShell, StatusBadge, toneForPaymentStatus, toneForRideStatus } from '../components/ui';
import { useI18n } from '../i18n/I18nProvider';
import { formatDateTime } from '../utils/dateFormat';

function formatName(user?: { email?: string | null; firstName?: string | null; lastName?: string | null } | null) {
  if (!user) return 'Unknown user';
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return name || user.email || 'Unknown user';
}

function formatMaybeDate(value?: string | null) {
  return value ? formatDateTime(value) : '—';
}

function formatMoney(cents?: number | null) {
  return new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR' }).format((cents || 0) / 100);
}

export const RideDetailsPage: React.FC = () => {
  const { t } = useI18n();
  const { request } = useApiClient();
  const { getToken } = useAuth();
  const params = useParams();
  const rideId = params.id as string;
  const [ride, setRide] = React.useState<AdminRideDetailsDto | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [chatMessages, setChatMessages] = React.useState<AdminRideChatMessageDto[]>([]);
  const [chatHistoryLoading, setChatHistoryLoading] = React.useState(false);
  const [chatHistoryError, setChatHistoryError] = React.useState<string | null>(null);
  const [chatLiveError, setChatLiveError] = React.useState<string | null>(null);
  const [chatConnected, setChatConnected] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAdminRide(request, rideId)
      .then((found) => { if (!cancelled) setRide(found); })
      .catch((e) => { if (!cancelled) setError(String(e.message || e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [request, rideId]);

  React.useEffect(() => {
    let cancelled = false;
    setChatHistoryLoading(true);
    setChatHistoryError(null);
    getAdminRideChat(request, rideId, { page: 0, size: 100 })
      .then((page) => {
        if (!cancelled) setChatMessages([...page.content].reverse());
      })
      .catch((e) => { if (!cancelled) setChatHistoryError(String(e.message || e)); })
      .finally(() => { if (!cancelled) setChatHistoryLoading(false); });
    return () => { cancelled = true; };
  }, [request, rideId]);

  React.useEffect(() => {
    let client: Client | null = null;
    let cancelled = false;
    getToken().then((token) => {
      if (cancelled || !token) return;
      const brokerURL = `${getApiBaseUrl().replace(/^http/i, 'ws')}/ws`;
      client = new Client({
        brokerURL,
        connectHeaders: { Authorization: `Bearer ${token}` },
        reconnectDelay: 5000,
        heartbeatIncoming: 10000,
        heartbeatOutgoing: 10000,
        onConnect: () => {
          setChatConnected(true);
          setChatLiveError(null);
          client?.subscribe(`/topic/rides/${rideId}/chat`, (frame) => {
            try {
              const live = JSON.parse(frame.body) as { senderId: string; senderName?: string; content: string; timestamp: string };
              const message: AdminRideChatMessageDto = {
                id: `live:${live.senderId}:${live.timestamp}`,
                sender: { id: live.senderId, firstName: live.senderName || undefined },
                senderRole: live.senderId === ride?.driver.id ? 'DRIVER' : 'RIDER',
                content: live.content,
                timestamp: live.timestamp,
              };
              setChatMessages((current) => current.some((item) => item.id === message.id) ? current : [...current, message]);
            } catch (_) {
              setChatLiveError('A live chat message could not be parsed.');
            }
          });
        },
        onDisconnect: () => setChatConnected(false),
        onStompError: (frame) => {
          setChatConnected(false);
          setChatLiveError(frame.headers.message || 'Live chat connection failed.');
        },
        onWebSocketClose: () => setChatConnected(false),
      });
      client.activate();
    }).catch((e) => setChatLiveError(String(e.message || e)));
    return () => {
      cancelled = true;
      setChatConnected(false);
      void client?.deactivate();
    };
  }, [getToken, ride?.driver.id, rideId]);

  return (
    <PageShell>
      <PageHeader
        title={ride ? <><span>{ride.departure.publicName}</span><span className="admin-ride-detail-title-arrow" aria-hidden="true">→</span><span>{ride.destination.publicName}</span></> : t('rides.title')}
        description={ride ? formatDateTime(ride.time) : 'Ride support and operations'}
        meta={ride && (
          <div className="admin-ride-detail-meta">
            <StatusBadge tone={toneForRideStatus(ride.status)}>{t(`rides.status.${ride.status.toLowerCase()}`)}</StatusBadge>
            <StatusBadge tone={ride.recurring ? 'violet' : 'slate'}>{t(ride.recurring ? 'rides.type.recurring' : 'rides.type.single')}</StatusBadge>
            <div className="admin-ride-detail-driver">
              <span>{formatName(ride.driver)}</span>
              <span>{ride.driver.email || ride.driver.id}</span>
            </div>
          </div>
        )}
        actions={<Link className="btn btn-neutral" to="/rides">Back to rides</Link>}
      />

      {loading && <div className="admin-panel admin-ride-detail-loading">{t('common.loading')}</div>}
      {error && <div className="admin-panel admin-ride-detail-error">{t('common.error')}: {error}</div>}

      {ride && (
        <div className="admin-ride-detail-content">
          <div className="admin-ride-detail-metrics">
            <MetricCard label="Bookings" value={ride.bookingCount} tone="violet" />
            <MetricCard label="Seats approved" value={`${ride.approvedSeats}/${ride.numberOfSeats}`} tone="blue" />
            <MetricCard label="Total price" value={formatMoney(ride.totalPriceInCents)} tone="slate" />
            <MetricCard label="Driver earnings" value={formatMoney(ride.totalDriverEarningsInCents)} tone="green" />
          </div>

          <div className="admin-ride-detail-context">
            <RideDetailSection title="Ride timeline">
              <div className="admin-ride-detail-facts">
                <InfoTile label="Created" value={formatMaybeDate(ride.createdAt)} />
                <InfoTile label="Started" value={formatMaybeDate(ride.startedAt)} />
                <InfoTile label="Completed" value={formatMaybeDate(ride.completedAt)} />
                <InfoTile label="Cancelled" value={formatMaybeDate(ride.cancelledAt)} />
                <InfoTile label="Expired" value={formatMaybeDate(ride.expiredAt)} />
              </div>
            </RideDetailSection>
            <div className="admin-ride-map-section">
              <h2>Route map</h2>
              <AdminRideRouteMap ride={ride} />
            </div>
          </div>

          <section className="admin-panel admin-ride-bookings">
            <header className="admin-ride-section-header">
              <h2>Bookings</h2>
              <StatusBadge tone="slate">{ride.bookings.length}</StatusBadge>
            </header>
            <div className="admin-table-scroll" tabIndex={0} aria-label="Bookings table">
              <table className="admin-table admin-ride-bookings-table">
                <thead>
                  <tr>
                    <th>Booking / request</th>
                    <th>Rider</th>
                    <th>Lifecycle</th>
                    <th>Payment</th>
                    <th>Ratings</th>
                  </tr>
                </thead>
                <tbody>
                  {ride.bookings.map((booking) => (
                    <tr key={booking.id}>
                      <td className="admin-ride-booking-identity">
                        <Link to={`/rides/bookings/${encodeURIComponent(booking.id)}`}>{booking.id}</Link>
                        {booking.rideRequestId && <div>{booking.rideRequestId}</div>}
                        <span>{booking.rideRequestStatus || '—'}</span>
                      </td>
                      <td className="admin-ride-booking-rider">
                        <strong>{formatName(booking.rider)}</strong>
                        <span>{booking.rider?.email || booking.rider?.id || '—'}</span>
                      </td>
                      <td><BookingLifecycle booking={booking} /></td>
                      <td className="admin-ride-booking-payment">
                        <StatusBadge tone={toneForPaymentStatus(booking.paymentCaptureStatus)}>{booking.paymentCaptureStatus || '—'}</StatusBadge>
                        <span>{formatMoney(booking.priceInCents)} · fee {formatMoney(booking.serviceFeeInCents)}</span>
                        {booking.paymentCaptureLastError && <strong>{booking.paymentCaptureLastError}</strong>}
                      </td>
                      <td><BookingReviews booking={booking} /></td>
                    </tr>
                  ))}
                  {ride.bookings.length === 0 && <tr><td colSpan={5} className="admin-ride-no-bookings">No bookings for this ride.</td></tr>}
                </tbody>
              </table>
            </div>
          </section>

          <section className="admin-panel admin-ride-chat">
            <header className="admin-ride-section-header admin-ride-chat-header">
              <div>
                <h2>Ride chat</h2>
                <p>Read only · {ride.chat.messageCount} stored messages · Last message {formatMaybeDate(ride.chat.lastMessageAt)}</p>
              </div>
              <StatusBadge tone={chatConnected ? 'green' : 'slate'}>{chatConnected ? 'Live monitoring' : 'History only'}</StatusBadge>
            </header>
            {chatHistoryError && <div className="admin-ride-chat-error">Chat history failed to load: {chatHistoryError}</div>}
            {chatLiveError && <div className="admin-ride-chat-live-error">{chatLiveError}</div>}
            <div className="admin-ride-chat-messages">
              {chatMessages.map((message) => (
                <article key={message.id} className="admin-ride-chat-message">
                  <header>
                    <div><strong>{formatName(message.sender)}</strong><StatusBadge tone="slate">{message.senderRole || '—'}</StatusBadge></div>
                    <time dateTime={message.timestamp}>{formatMaybeDate(message.timestamp)}</time>
                  </header>
                  <p>{message.content}</p>
                </article>
              ))}
              {chatHistoryLoading && chatMessages.length === 0 && <div className="admin-ride-chat-empty">Loading chat history…</div>}
              {!chatHistoryLoading && !chatHistoryError && chatMessages.length === 0 && <div className="admin-ride-chat-empty">No chat messages.</div>}
            </div>
          </section>
        </div>
      )}
    </PageShell>
  );
};

function RideDetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="admin-panel admin-ride-detail-section">
      <h2 className="admin-ride-section-title">{title}</h2>
      <div className="admin-ride-detail-section-content">{children}</div>
    </section>
  );
}

function BookingLifecycle({ booking }: { booking: AdminRideDetailsDto['bookings'][number] }) {
  const events = [
    { label: 'Approved', date: booking.approvedAt },
    { label: 'Rejected', date: booking.rejectedAt },
    { label: 'Picked up', date: booking.pickedUpAt },
    { label: 'Dropped off', date: booking.completedAt },
    { label: 'Cancelled', date: booking.cancelledAt },
    { label: 'Expired', date: booking.expiredAt },
  ].filter((event): event is { label: string; date: string } => Boolean(event.date));
  if (events.length === 0) return <span className="admin-ride-booking-muted">{booking.rideRequestStatus || 'No lifecycle events'}</span>;
  return (
    <details className="admin-ride-booking-lifecycle">
      <summary>{events.length} lifecycle steps</summary>
      <ul>{events.map(({ label, date }) => <li key={label}><span>{label}</span><time dateTime={date}>{formatMaybeDate(date)}</time></li>)}</ul>
    </details>
  );
}

function BookingReviews({ booking }: { booking: AdminRideDetailsDto['bookings'][number] }) {
  const count = Number(Boolean(booking.driverReviewOfRider)) + Number(Boolean(booking.riderReviewOfDriver));
  return (
    <details className="admin-ride-booking-reviews">
      <summary>Reviews · {count}</summary>
      <ReviewLine label="Driver → rider" review={booking.driverReviewOfRider} />
      <ReviewLine label="Rider → driver" review={booking.riderReviewOfDriver} />
    </details>
  );
}

function ReviewLine({ label, review }: { label: string; review?: AdminRideReviewDto | null }) {
  if (!review) return <div className="mb-2"><span className="font-semibold">{label}:</span> -</div>;
  const rating = review.starRating != null ? `${'★'.repeat(review.starRating)}${'☆'.repeat(Math.max(0, 5 - review.starRating))}` : review.state;
  return (
    <div className="mb-2 rounded border border-gray-100 bg-gray-50 p-2">
      <div><span className="font-semibold">{label}:</span> <span className="text-amber-600">{rating}</span></div>
      {review.comment && <div className="mt-1 whitespace-pre-wrap text-gray-600">{review.comment}</div>}
    </div>
  );
}

export default RideDetailsPage;
